/**
 * Centralized API endpoints configuration
 */

export const API_PREFIX = "/api/v1" as const;

export const ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: `${API_PREFIX}/auth/login`,
    GOOGLE: `${API_PREFIX}/auth/google`,
    LOGOUT: `${API_PREFIX}/auth/logout`,
    REFRESH: `${API_PREFIX}/auth/refresh`,
    PROFILE: `${API_PREFIX}/auth/profile`,
    REGISTER: `${API_PREFIX}/auth/register`,
    SEND_SIGNUP_OTP: `${API_PREFIX}/auth/send-signup-otp`,
    VERIFY_SIGNUP_OTP: `${API_PREFIX}/auth/verify-signup-otp`,
    FORGOT_PASSWORD: `${API_PREFIX}/auth/forgot-password`,
    RESET_PASSWORD: `${API_PREFIX}/auth/reset-password`,
    CHECK_SHOP_NAME: `${API_PREFIX}/auth/check-shop-name`,
    ME: `${API_PREFIX}/me`,
    SYNC: `${API_PREFIX}/sync`,
  },

  // User endpoints
  USERS: {
    BASE: `${API_PREFIX}/users`,
    byId: (id: string) => `${API_PREFIX}/users/${id}`,
    PROFILE: `${API_PREFIX}/users/profile`,
    UPDATE_PROFILE: `${API_PREFIX}/users/profile`,
    CHANGE_PASSWORD: `${API_PREFIX}/profile/change-password`,
    AVATAR: `${API_PREFIX}/users/avatar`,
    SEARCH: `${API_PREFIX}/users/search`,
  },

  // Product endpoints
  PRODUCTS: {
    BASE: `${API_PREFIX}/products`,
    byId: (id: string) => `${API_PREFIX}/products/${id}`,
    SEARCH: `${API_PREFIX}/products/search`,
    CATEGORIES: `${API_PREFIX}/products/categories`,
    byCategory: (categoryId: string) =>
      `${API_PREFIX}/products/category/${encodeURIComponent(categoryId)}`,
    FEATURED: `${API_PREFIX}/products/featured`,
    TOP_DEALS: `${API_PREFIX}/products/top-deals`,
    TOP_RANKING: `${API_PREFIX}/products/top-ranking`,
    NEW_ARRIVALS: `${API_PREFIX}/products/new-arrivals`,
    POPULAR_SUGGESTIONS: `${API_PREFIX}/products/popular-suggestions`,
    FREQUENTLY_SEARCHED: `${API_PREFIX}/products/frequently-searched`,
    HERO_PROMO_ITEMS: `${API_PREFIX}/products/hero-promo-items`,
    UPLOAD_IMAGE: `${API_PREFIX}/products/upload-image`,
    BULK_UPDATE: `${API_PREFIX}/products/bulk-update`,
    BULK_DELETE: `${API_PREFIX}/products/bulk-delete`,
  },

  // Categories endpoints (public - accessible to all authenticated users)
  CATEGORIES: {
    BASE: `${API_PREFIX}/categories`,
  },

  // Sellers endpoints (public - accessible to all authenticated users)
  SELLERS: {
    BASE: `${API_PREFIX}/sellers`,
    byId: (id: string) => `${API_PREFIX}/sellers/${id}`,
    SEARCH: `${API_PREFIX}/sellers/search`,
    TOP: `${API_PREFIX}/sellers/top`,
    FILTER_OPTIONS: `${API_PREFIX}/sellers/filter-options`,
  },

  // Chat endpoints (Buyer ↔ Seller DM)
  CHAT: {
    CONVERSATIONS: `${API_PREFIX}/chat/conversations`,
    conversationMessages: (conversationId: string) =>
      `${API_PREFIX}/chat/conversations/${conversationId}/messages`,
    markRead: (conversationId: string) =>
      `${API_PREFIX}/chat/conversations/${conversationId}/read`,
    ATTACHMENTS: `${API_PREFIX}/chat/attachments`,
    attachmentUrl: (key: string) =>
      `${API_PREFIX}/chat/attachments/${encodeURIComponent(key)}/url`,
  },

  NOTIFICATIONS: {
    BASE: `${API_PREFIX}/notifications`,
    UNREAD_COUNT: `${API_PREFIX}/notifications/unread-count`,
    MARK_READ: (id: string) => `${API_PREFIX}/notifications/${id}/read`,
    MARK_ALL_READ: `${API_PREFIX}/notifications/read-all`,
  },

  // Admin Chat (monitoring – admin only)
  ADMIN_CHAT: {
    CONVERSATIONS: `${API_PREFIX}/admin/chat/conversations`,
    conversationMessages: (id: string) =>
      `${API_PREFIX}/admin/chat/conversations/${id}/messages`,
    attachmentUrl: (key: string) =>
      `${API_PREFIX}/admin/chat/attachments/${encodeURIComponent(key)}/url`,
  },

  // Order endpoints
  ORDERS: {
    BASE: `${API_PREFIX}/orders`,
    byId: (id: string) => `${API_PREFIX}/orders/${id}`,
    USER_ORDERS: `${API_PREFIX}/orders/user`,
    STATUS: `${API_PREFIX}/orders/status`,
    byStatus: (status: string) => `${API_PREFIX}/orders/status/${status}`,
    INVOICE: (id: string) => `${API_PREFIX}/orders/${id}/invoice`,
    TRACKING: (id: string) => `${API_PREFIX}/orders/${id}/tracking`,
    CANCEL: (id: string) => `${API_PREFIX}/orders/${id}/cancel`,
  },

  // Company endpoints
  COMPANIES: {
    BASE: `${API_PREFIX}/companies`,
    byId: (id: string) => `${API_PREFIX}/companies/${id}`,
    SEARCH: `${API_PREFIX}/companies/search`,
    VERIFY: `${API_PREFIX}/companies/verify`,
  },

  // File upload endpoints
  UPLOAD: {
    BASE: `${API_PREFIX}/upload`,
    IMAGE: `${API_PREFIX}/upload/image`,
    DOCUMENT: `${API_PREFIX}/upload/document`,
    BULK: `${API_PREFIX}/upload/bulk`,
  },

  // Admin endpoints
  ADMIN: {
    BASE: `${API_PREFIX}/admin`,
    ALL_USERS: `${API_PREFIX}/admin/users`,
    PENDING_USERS: `${API_PREFIX}/admin/users/pending`,
    CREATE_USER: `${API_PREFIX}/admin/users`,
    USER_BY_ID: (id: string) => `${API_PREFIX}/admin/users/${id}`,
    UPDATE_USER: (id: string) => `${API_PREFIX}/admin/users/${id}`,
    DELETE_USER: (id: string) => `${API_PREFIX}/admin/users/${id}`,
    APPROVE_USER: (id: string) => `${API_PREFIX}/admin/users/${id}/approve`,
    REJECT_USER: (id: string) => `${API_PREFIX}/admin/users/${id}/reject`,
    SUSPEND_USER: (id: string) => `${API_PREFIX}/admin/users/${id}/suspend`,
    ACTIVATE_USER: (id: string) => `${API_PREFIX}/admin/users/${id}/activate`,
    UPDATE_USER_ROLE: (id: string) => `${API_PREFIX}/admin/users/${id}/role`,
    RESET_USER_PASSWORD: (id: string) =>
      `${API_PREFIX}/admin/users/${id}/reset-password`,
    BULK_USER_ACTION: `${API_PREFIX}/admin/users/bulk-action`,
    EXPORT_USERS: `${API_PREFIX}/admin/users/export`,
    USER_STATISTICS: `${API_PREFIX}/admin/users/statistics`,
    DATABASE_STATS: `${API_PREFIX}/admin/database-stats`,
    DASHBOARD_STATISTICS: `${API_PREFIX}/admin/dashboard/statistics`,
    DASHBOARD_RECENT_ACTIVITY: `${API_PREFIX}/admin/dashboard/recent-activity`,
    CLEAR_ALL_DATA: `${API_PREFIX}/admin/clear-all-data`,
    RESET_DATABASE: `${API_PREFIX}/admin/reset-database`,
    // Admin Product endpoints
    PRODUCTS: `${API_PREFIX}/admin/products`,
    PRODUCT_STATISTICS: `${API_PREFIX}/admin/products/statistics`,
    PRODUCT_AUTO_APPROVE_SETTING: `${API_PREFIX}/admin/products/settings/auto-approve`,
    PRODUCT_BY_ID: (id: string) => `${API_PREFIX}/admin/products/${id}`,
    PRODUCT_PENDING_REVIEW: (id: string) =>
      `${API_PREFIX}/admin/products/${id}/pending-review`,
    PRODUCT_SELLER_UPDATE_HISTORY: (id: string) =>
      `${API_PREFIX}/admin/products/${id}/seller-update-history`,
    ASSIGN_PRODUCT: (id: string) => `${API_PREFIX}/admin/products/${id}/assign`,
    APPROVE_PRODUCT: (id: string) =>
      `${API_PREFIX}/admin/products/${id}/approve`,
    REJECT_PRODUCT: (id: string) => `${API_PREFIX}/admin/products/${id}/reject`,
    DUPLICATE_PRODUCT: (id: string) =>
      `${API_PREFIX}/admin/products/${id}/duplicate`,
    AVAILABLE_SELLERS: `${API_PREFIX}/admin/products/sellers/available`,
    SAVE_DRAFT: `${API_PREFIX}/admin/products/save-draft`,
    PUBLISH: `${API_PREFIX}/admin/products/publish`,
    UPDATE_TO_DRAFT: (id: string) =>
      `${API_PREFIX}/admin/products/${id}/save-draft`,
    UPDATE_TO_PUBLISHED: (id: string) =>
      `${API_PREFIX}/admin/products/${id}/publish`,
    // Admin Category endpoints
    CATEGORIES: `${API_PREFIX}/admin/categories`,
    CATEGORY_BY_ID: (id: string) => `${API_PREFIX}/admin/categories/${id}`,
    // Admin Media endpoints
    MEDIA: {
      UPLOAD: `${API_PREFIX}/admin/media/upload`,
      LIST: `${API_PREFIX}/admin/media`,
      FOLDERS: `${API_PREFIX}/admin/media/folders`,
      FOLDER_BY_SLUG: (slug: string) =>
        `${API_PREFIX}/admin/media/folders/${encodeURIComponent(slug)}`,
      MOVE_TO_FOLDER: (id: string) => `${API_PREFIX}/admin/media/${id}/folder`,
      BY_ID: (id: string) => `${API_PREFIX}/admin/media/${id}`,
      DOWNLOAD: (id: string) => `${API_PREFIX}/admin/media/${id}/download`,
      DELETE: (id: string) => `${API_PREFIX}/admin/media/${id}`,
    },
    // Admin Seller endpoints
    SELLERS: `${API_PREFIX}/admin/sellers`,
    SELLER_STATUS: (id: string) => `${API_PREFIX}/admin/seller/${id}/status`,
    // Admin Reports endpoints
    REPORTS: {
      METRICS: `${API_PREFIX}/admin/reports/metrics`,
      SALES_TREND: `${API_PREFIX}/admin/reports/sales-trend`,
      USER_GROWTH: `${API_PREFIX}/admin/reports/user-growth`,
      CATEGORY_DISTRIBUTION: `${API_PREFIX}/admin/reports/category-distribution`,
      GENERATED: `${API_PREFIX}/admin/reports/generated`,
      EXPORT: `${API_PREFIX}/admin/reports/export`,
    },
  },

  // Seller endpoints
  SELLER: {
    BASE: `${API_PREFIX}/seller`,
    // Seller Product endpoints
    PRODUCTS: `${API_PREFIX}/seller/products`,
    PRODUCT_BY_ID: (id: string) => `${API_PREFIX}/seller/products/${id}`,
    PRODUCT_SELLER_UPDATE_HISTORY: (id: string) =>
      `${API_PREFIX}/seller/products/${id}/seller-update-history`,
    SAVE_DRAFT: `${API_PREFIX}/seller/products/save-draft`,
    UPDATE_TO_DRAFT: (id: string) =>
      `${API_PREFIX}/seller/products/${id}/save-draft`,
    SUBMIT_FOR_APPROVAL: (id: string) =>
      `${API_PREFIX}/seller/products/${id}/submit`,
    PRODUCT_STATISTICS: `${API_PREFIX}/seller/products/statistics`,
    BULK_ACTIONS: `${API_PREFIX}/seller/products/bulk-actions`,
    DUPLICATE_PRODUCT: (id: string) =>
      `${API_PREFIX}/seller/products/${id}/duplicate`,
    // Seller Media endpoints (reuses admin/media endpoints)
    MEDIA: {
      UPLOAD: `${API_PREFIX}/admin/media/upload`,
      LIST: `${API_PREFIX}/admin/media`,
      FOLDERS: `${API_PREFIX}/admin/media/folders`,
      FOLDER_BY_SLUG: (slug: string) =>
        `${API_PREFIX}/admin/media/folders/${encodeURIComponent(slug)}`,
      MOVE_TO_FOLDER: (id: string) => `${API_PREFIX}/admin/media/${id}/folder`,
      BY_ID: (id: string) => `${API_PREFIX}/admin/media/${id}`,
      DOWNLOAD: (id: string) => `${API_PREFIX}/admin/media/${id}/download`,
      DELETE: (id: string) => `${API_PREFIX}/admin/media/${id}`,
    },
    // Seller Profile endpoints (reuses profile endpoints)
    PROFILE: `${API_PREFIX}/profile`,
    UPDATE_PROFILE: `${API_PREFIX}/profile`,
    UPLOAD_FILE: `${API_PREFIX}/profile/upload`,
  },

  // Storefront endpoints
  STOREFRONT: {
    SETTINGS: `${API_PREFIX}/storefront/settings`,
    BANNERS: `${API_PREFIX}/storefront/banners`,
    BANNER_BY_ID: (id: string) => `${API_PREFIX}/storefront/banners/${id}`,
    ANALYTICS: `${API_PREFIX}/storefront/analytics`,
    REVIEWS: `${API_PREFIX}/storefront/reviews`,
    REVIEW_APPROVE: (id: string) =>
      `${API_PREFIX}/storefront/reviews/${id}/approve`,
    REVIEW_REJECT: (id: string) =>
      `${API_PREFIX}/storefront/reviews/${id}/reject`,
    INQUIRIES: `${API_PREFIX}/storefront/inquiries`,
    INQUIRIES_ALL: `${API_PREFIX}/storefront/inquiries/all`,
    INQUIRIES_MY: `${API_PREFIX}/storefront/inquiries/my`,
    INQUIRY_BY_ID: (id: string) => `${API_PREFIX}/storefront/inquiries/${id}`,
    TESTIMONIALS: `${API_PREFIX}/storefront/testimonials`,
    TESTIMONIAL_BY_ID: (id: string) =>
      `${API_PREFIX}/storefront/testimonials/${id}`,
    PROMOTIONAL_BANNERS: `${API_PREFIX}/storefront/promotional-banners`,
    PROMOTIONAL_BANNER_BY_ID: (id: string) =>
      `${API_PREFIX}/storefront/promotional-banners/${id}`,
    DISCOUNTS: `${API_PREFIX}/storefront/discounts`,
    DISCOUNT_BY_ID: (id: string) => `${API_PREFIX}/storefront/discounts/${id}`,
    ORDERS: `${API_PREFIX}/storefront/orders`,
    ORDER_STATUS: (id: string) =>
      `${API_PREFIX}/storefront/orders/${id}/status`,
    PUBLIC_BY_SELLER_ID: (sellerId: string) =>
      `${API_PREFIX}/storefront/public/${sellerId}`,
    PUBLIC_PRODUCTS_BY_SELLER_ID: (sellerId: string) =>
      `${API_PREFIX}/storefront/public/${sellerId}/products`,
  },

  // Favorites endpoints
  FAVORITES: {
    BASE: `${API_PREFIX}/favorites`,
    add: (productId: string) => `${API_PREFIX}/favorites/${productId}`,
    remove: (productId: string) => `${API_PREFIX}/favorites/${productId}`,
  },

  // RFQ endpoints
  RFQ: {
    BASE: `${API_PREFIX}/rfq`,
    byId: (id: string) => `${API_PREFIX}/rfq/${id}`,
    quotes: (rfqId: string) => `${API_PREFIX}/rfq/${rfqId}/quotes`,
    quoteById: (rfqId: string, quoteId: string) =>
      `${API_PREFIX}/rfq/${rfqId}/quotes/${quoteId}`,
    acceptQuote: (rfqId: string, quoteId: string) =>
      `${API_PREFIX}/rfq/${rfqId}/quotes/${quoteId}/accept`,
    rejectQuote: (rfqId: string, quoteId: string) =>
      `${API_PREFIX}/rfq/${rfqId}/quotes/${quoteId}/reject`,
    SELLER_QUOTES: `${API_PREFIX}/rfq/quotes/my`,
    METRICS: `${API_PREFIX}/admin/rfq/metrics`,
    ADMIN_LIST: `${API_PREFIX}/admin/rfq`,
    ADMIN_FORCE_CLOSE: (rfqId: string) =>
      `${API_PREFIX}/admin/rfq/${rfqId}/force-close`,
    ADMIN_SUSPEND_SELLER: (sellerId: string) =>
      `${API_PREFIX}/admin/rfq/sellers/${sellerId}/suspend`,
    ADMIN_EXPORT: `${API_PREFIX}/admin/rfq/export`,
  },

  HOMEPAGE: {
    PUBLIC: `${API_PREFIX}/homepage`,
    SECTION_META: (slug: string) =>
      `${API_PREFIX}/homepage/sections/by-slug/${encodeURIComponent(slug)}`,
    SECTION_PRODUCTS: (slug: string) =>
      `${API_PREFIX}/homepage/sections/by-slug/${encodeURIComponent(slug)}/products`,
    BANNER_PRODUCTS: (slug: string) =>
      `${API_PREFIX}/homepage/banners/by-slug/${encodeURIComponent(slug)}/products`,
    ADMIN_SECTIONS: `${API_PREFIX}/admin/homepage/sections`,
    ADMIN_SECTION: (id: string) =>
      `${API_PREFIX}/admin/homepage/sections/${id}`,
    ADMIN_SECTIONS_REORDER: `${API_PREFIX}/admin/homepage/sections/reorder`,
    ADMIN_SECTION_PRODUCTS: (id: string) =>
      `${API_PREFIX}/admin/homepage/sections/${id}/products`,
    ADMIN_BANNERS: `${API_PREFIX}/admin/homepage/banners`,
    ADMIN_BANNER: (id: string) =>
      `${API_PREFIX}/admin/homepage/banners/${id}`,
    ADMIN_BANNER_PRODUCTS: (id: string) =>
      `${API_PREFIX}/admin/homepage/banners/${id}/products`,
    ADMIN_BANNERS_REORDER: `${API_PREFIX}/admin/homepage/banners/reorder`,
  },
} as const;

export type EndpointKey = keyof typeof ENDPOINTS;
export type EndpointPath = (typeof ENDPOINTS)[keyof typeof ENDPOINTS];
